import { useCallback, useEffect, useMemo } from 'react';
import type { Key } from 'react-aria-components';
import { useListData } from 'react-stately';

import { MultiSelect } from '@/lib/base/forms/select/multi-select';
import type { SelectItemType } from '@/lib/base/forms/select/select';

interface User {
  id: number;
  email: string;
  display: string;
}

interface UserAssignmentPickerProps {
  selectedUserIds: number[];
  onChange: (userIds: number[]) => void;
  availableUsers: User[];
  isDisabled?: boolean;
  label?: string;
  placeholder?: string;
}

const renderItem = (item: SelectItemType) => (
  <MultiSelect.Item
    id={item.id}
    label={item.label}
    supportingText={item.supportingText}
  />
);

export default function UserAssignmentPicker({
  selectedUserIds,
  onChange,
  availableUsers,
  isDisabled = false,
  label = 'Recipients',
  placeholder = 'Search by name or email',
}: UserAssignmentPickerProps) {
  const items = useMemo<SelectItemType[]>(
    () =>
      availableUsers.map(user => ({
        id: String(user.id),
        label: user.display || user.email.split('@')[0],
        supportingText: user.email,
      })),
    [availableUsers]
  );

  const selectedIds = useMemo(
    () => selectedUserIds.map(String),
    [selectedUserIds]
  );

  // useListData reads initialItems on first render only; every later change to
  // selectedUserIds is reconciled by the sync effect below.
  const selectedItems = useListData<SelectItemType>({
    initialItems: selectedIds
      .map(id => items.find(item => item.id === id))
      .filter((item): item is SelectItemType => item !== undefined),
  });

  useEffect(() => {
    const currentIds = selectedItems.items.map(item => String(item.id));
    if (JSON.stringify(currentIds) === JSON.stringify(selectedIds)) {
      return;
    }
    const desiredItems = selectedIds
      .map(id => items.find(item => item.id === id))
      .filter((item): item is SelectItemType => item !== undefined);
    if (currentIds.length > 0) {
      selectedItems.remove(...currentIds);
    }
    if (desiredItems.length > 0) {
      selectedItems.append(...desiredItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedItems from useListData is a new object each render; reading it via closure is safe because deps change whenever the value we need to sync changes
  }, [selectedIds, items]);

  const handleItemInserted = useCallback(
    (key: Key) => {
      const userId = Number(key);
      if (selectedUserIds.includes(userId)) {
        return;
      }
      onChange([...selectedUserIds, userId]);
    },
    [selectedUserIds, onChange]
  );

  const handleItemCleared = useCallback(
    (key: Key) => {
      const userId = Number(key);
      onChange(selectedUserIds.filter(id => id !== userId));
    },
    [selectedUserIds, onChange]
  );

  return (
    <MultiSelect
      label={label}
      placeholder={placeholder}
      shortcut={false}
      items={items}
      selectedItems={selectedItems}
      onItemInserted={handleItemInserted}
      onItemCleared={handleItemCleared}
      isDisabled={isDisabled}
      size='sm'
      emptyText='No people found'
    >
      {renderItem}
    </MultiSelect>
  );
}
